/**
 * SIMBAD Client for Astronomical Object Queries
 * 
 * This module provides functionality to query the SIMBAD astronomical database
 * for objects not found in the local database.
 */

// SIMBAD script errors meaning "no object by this identifier" (as opposed to a failed request)
const UNKNOWN_IDENTIFIER = /No known catalog could be found|Identifier not found|incorrect format for catalogs/i;

export class SimbadClient {
    constructor() {
        this.baseUrl = 'https://simbad.cds.unistra.fr/simbad/sim-tap/sync';
        this.nameResolverUrl = 'https://simbad.cds.unistra.fr/simbad/sim-id';
        this.corsProxy = 'https://corsproxy.io/?';
    }

    /**
     * Generate common name variants to maximize SIMBAD resolution success.
     * Handles Sharpless (Sh 2-XX) normalization and several casing/spacing styles.
     */
    nameVariants(objectName) {
        const variants = new Set();
        const raw = (objectName || '').trim();
        if (!raw) return [];

        variants.add(raw);
        variants.add(raw.toUpperCase());
        variants.add(raw.toLowerCase());

        // Normalize multiple spaces/hyphens
        const compact = raw.replace(/\s+/g, ' ').replace(/\s*-\s*/g, '-').trim();
        variants.add(compact);

        // Sharpless catalog normalizations (sh2-29 -> Sh 2-29, Sh2-29, Sh 2 29)
        const sh = raw.toLowerCase().replace(/\s+/g, '');
        const m = sh.match(/^sh(\d+)[- ]?(\d{1,3})$/); // sh2-29, sh2 29
        if (m) {
            const cat = m[1];
            const idx = m[2].replace(/^0+/, '');
            const base = `Sh ${cat}-${idx}`;
            variants.add(base);
            variants.add(base.replace(' ', ''));// Sh2-29
            variants.add(`Sh ${cat} ${idx}`);
            variants.add(`SH ${cat}-${idx}`);
        }

        // RCW catalog normalizations (RCW 86 -> RCW 86, RCW86, RCW 086)
        const rcwMatch = raw.match(/^rcw\s*(\d+)$/i);
        if (rcwMatch) {
            const num = rcwMatch[1].replace(/^0+/, ''); // Remove leading zeros
            variants.add(`RCW ${num}`);
            variants.add(`RCW${num}`);
            variants.add(`RCW ${num.padStart(3, '0')}`); // Try with leading zeros (RCW 086)
            variants.add(`[RCW] ${num}`); // Sometimes SIMBAD uses brackets
        }

        return Array.from(variants);
    }

    /**
     * Query SIMBAD for object coordinates
     * @param {string} objectName - Name of the astronomical object
     * @returns {Promise<Object|null>} Object coordinates or null if not found
     */
    async getObjectCoordinates(objectName) {
        try {
            console.log(`Querying SIMBAD for: ${objectName}`);
            
            // Use SIMBAD's basic query interface with text output
            const url = `https://simbad.cds.unistra.fr/simbad/sim-basic?Ident=${encodeURIComponent(objectName)}&output.format=ASCII`;
            
            let response;
            try {
                response = await fetch(url);
            } catch (error) {
                console.log('Direct SIMBAD request failed, trying CORS proxy...');
                response = await fetch(`${this.corsProxy}${encodeURIComponent(url)}`);
            }

            if (!response.ok) {
                console.log(`SIMBAD query failed with status: ${response.status}`);
                return null;
            }

            const text = await response.text();
            console.log('SIMBAD response text:', text.substring(0, 500));
            
            // Look for coordinates in the response
            // SIMBAD format: "Coordinates(ICRS,ep=J2000,eq=2000): 20 42 45.76 +68 09 12.7"
            const coordsMatch = text.match(/Coordinates\([^)]+\):\s*([\d\s.-]+)/i);
            
            if (coordsMatch) {
                const coordsStr = coordsMatch[1].trim();
                console.log('Found coordinates string:', coordsStr);
                
                // Parse "HH MM SS.SS +DD MM SS.S" format
                const parts = coordsStr.split(/\s+/);
                if (parts.length >= 6) {
                    try {
                        const raH = parseFloat(parts[0]);
                        const raM = parseFloat(parts[1]);
                        const raS = parseFloat(parts[2]);
                        const decD = parseFloat(parts[3]);
                        const decM = parseFloat(parts[4]);
                        const decS = parseFloat(parts[5]);
                        
                        const raHours = raH + raM/60 + raS/3600;
                        const decSign = parts[3].startsWith('-') ? -1 : 1;
                        const decDegrees = Math.abs(decD) + decM/60 + decS/3600;
                        const finalDecDegrees = decSign * decDegrees;
                        if (!this.isValidSkyPosition(raHours * 15.0, finalDecDegrees)) {
                            return null;
                        }
                        
                        const result = {
                            name: objectName,
                            ra_deg: raHours * 15.0,
                            dec_deg: finalDecDegrees,
                            ra_hours: raHours,
                            dec_degrees: finalDecDegrees,
                            object_type: 'Unknown',
                            magnitude: null,
                            catalog: 'SIMBAD',
                            description: `${objectName} from SIMBAD`,
                            source: 'SIMBAD'
                        };
                        
                        console.log(`Found in SIMBAD (basic):`, result);
                        return result;
                    } catch (parseError) {
                        console.error('Error parsing coordinates:', parseError);
                    }
                }
            }
            
            console.log('No coordinates found in SIMBAD response');
            return null;
        } catch (error) {
            console.error('SIMBAD basic query error:', error);
            return null;
        }
    }

    /**
     * Whether a position is on the sky (guards against parsing error text as coordinates)
     * @param {number} raDeg - Right ascension in degrees
     * @param {number} decDeg - Declination in degrees
     * @returns {boolean}
     */
    isValidSkyPosition(raDeg, decDeg) {
        return Number.isFinite(raDeg) && Number.isFinite(decDeg)
            && raDeg >= 0 && raDeg < 360 && decDeg >= -90 && decDeg <= 90;
    }

    /**
     * Alternative SIMBAD query using name resolver (more reliable)
     * @param {string} objectName - Name of the astronomical object
     * @returns {Promise<Object|null>} Object coordinates or null if not found
     */
    async getObjectCoordinatesSimple(objectName) {
        return (await this.queryScriptResolver(objectName)).result;
    }

    /**
     * Resolve a name with SIMBAD's script interface
     * @param {string} objectName - Name of the astronomical object
     * @returns {Promise<{result: Object|null, unknown: boolean}>} unknown is true when
     *   SIMBAD answered that it has no object by this identifier (not on network errors)
     */
    async queryScriptResolver(objectName) {
        try {
            console.log(`Querying SIMBAD name resolver for: ${objectName}`);
            
            // Use SIMBAD's script interface which is more reliable
            const scriptQuery = `format object "%IDLIST(1) | %COO(A D;ICRS;J2000;2000) | %OTYPE"
query id ${objectName}`;
            
            const params = new URLSearchParams({
                'script': scriptQuery
            });
            
            const url = 'https://simbad.cds.unistra.fr/simbad/sim-script';
            
            let response;
            try {
                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: params
                });
            } catch (error) {
                console.log('Direct SIMBAD request failed, trying CORS proxy...');
                response = await fetch(`${this.corsProxy}${encodeURIComponent(url)}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: params
                });
            }

            if (!response.ok) {
                throw new Error(`SIMBAD script query failed: ${response.status}`);
            }

            const text = await response.text();
            console.log('SIMBAD response:', text);
            
            // Parse the response - we expect a section with lines like: "NAME | RA DEC | TYPE"
            const dataStart = text.indexOf('::data::');
            if (dataStart < 0) {
                // No data section: SIMBAD didn't resolve the name. Don't parse the echoed
                // script ("%COO(...J2000;2000)") as coordinates.
                console.log(`SIMBAD could not resolve '${objectName}'`);
                return { result: null, unknown: UNKNOWN_IDENTIFIER.test(text) };
            }
            const body = text.slice(dataStart);
            const lines = body.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('::') && l.includes('|'));
            console.log(`Processing ${lines.length} data lines from SIMBAD response`);

            let foundResult = null;
            for (const line of lines) {
                console.log(`Processing line: "${line}"`);
                const parts = line.split('|').map(part => part.trim());
                console.log(`Line parts (${parts.length}):`, parts);
                if (parts.length >= 3) {
                    const name = parts[0];
                    const coordsStr = parts[1];
                    const objectType = parts[2];
                    
                    // Tokenize all numbers (handles various formats)
                    const tokens = coordsStr.match(/[-+]?\d*\.?\d+/g) || [];
                    const nums = tokens.map(parseFloat);
                    // Sign from the text: parseFloat('-00') is -0, which isn't < 0
                    const negative = (i) => tokens[i].startsWith('-');
                    console.log(`Parsing coordinates for ${name}: "${coordsStr}" -> numbers:`, nums);

                    let raHours = null;
                    let decDeg = null;
                    if (nums.length >= 6) {
                        // HH MM SS.S  DD MM SS.S
                        raHours = nums[0] + nums[1]/60 + nums[2]/3600;
                        const sign = negative(3) ? -1 : 1;
                        decDeg = sign * (Math.abs(nums[3]) + nums[4]/60 + nums[5]/3600);
                    } else if (nums.length === 5) {
                        // HH MM SS  DD MM.m  (like "14 40 29 -62 38.7")
                        raHours = nums[0] + nums[1]/60 + nums[2]/3600;
                        const sign = negative(3) ? -1 : 1;
                        decDeg = sign * (Math.abs(nums[3]) + nums[4]/60);
                    } else if (nums.length === 4) {
                        // HH MM.m  DD MM  (common in SIMBAD script)
                        raHours = nums[0] + nums[1]/60;
                        const sign = negative(2) ? -1 : 1;
                        decDeg = sign * (Math.abs(nums[2]) + nums[3]/60);
                    } else if (nums.length === 2) {
                        // RA(deg) DEC(deg) (fallback)
                        const raDeg = nums[0];
                        raHours = raDeg / 15.0;
                        decDeg = nums[1];
                    }

                    console.log(`Calculated: RA=${raHours}h, Dec=${decDeg}°`);
                    
                    if (raHours !== null && decDeg !== null && this.isValidSkyPosition(raHours * 15.0, decDeg)) {
                        const result = {
                            name: name,
                            ra_deg: raHours * 15.0,
                            dec_deg: decDeg,
                            ra_hours: raHours,
                            dec_degrees: decDeg,
                            object_type: objectType || 'Unknown',
                            magnitude: null,
                            catalog: 'SIMBAD',
                            description: `${name} from SIMBAD`,
                            source: 'SIMBAD'
                        };
                        console.log(`Found in SIMBAD (script):`, result);
                        foundResult = result;
                        break; // Exit loop once we find a valid result
                    }
                }
            }

            if (foundResult) {
                console.log(`Returning SIMBAD result:`, foundResult);
                return { result: foundResult, unknown: false };
            }

            console.log(`No valid coordinates found in SIMBAD script response`);
            return { result: null, unknown: false };
        } catch (error) {
            console.error('SIMBAD script query error:', error);
            return { result: null, unknown: false };
        }
    }

    /**
     * SIMBAD sim-id JSON endpoint fallback. Attempts to parse jradeg/jdedeg or similar fields.
     */
    async getObjectCoordinatesSimIdJSON(objectName) {
        try {
            const url = `https://simbad.cds.unistra.fr/simbad/sim-id?Ident=${encodeURIComponent(objectName)}&output.format=JSON`;
            let response;
            try {
                response = await fetch(url);
            } catch {
                response = await fetch(`${this.corsProxy}${encodeURIComponent(url)}`);
            }
            if (!response.ok) return null;
            // The endpoint sometimes returns HTML; guard JSON parsing.
            const raw = await response.text();
            let data;
            try {
                data = JSON.parse(raw);
            } catch {
                return null;
            }

            // Deep search for common coordinate keys in degrees
            let raDeg = null, decDeg = null, name = null, otype = null;
            const visit = (node) => {
                if (!node || typeof node !== 'object') return;
                for (const [k, v] of Object.entries(node)) {
                    const key = k.toLowerCase();
                    if (key.includes('jradeg') || key === 'ra_d' || key === 'ra_deg' || key === 'radeg') {
                        const val = parseFloat(v);
                        if (isFinite(val)) raDeg = val;
                    } else if (key.includes('jdedeg') || key === 'dec_d' || key === 'dec_deg' || key === 'ddeg' || key === 'decdeg') {
                        const val = parseFloat(v);
                        if (isFinite(val)) decDeg = val;
                    } else if (!name && (key.includes('main_id') || key === 'id' || key === 'mainid')) {
                        if (typeof v === 'string' && v.trim()) name = v.trim();
                    } else if (!otype && (key.includes('otype') || key === 'otype_txt')) {
                        if (typeof v === 'string' && v.trim()) otype = v.trim();
                    }
                    if (v && typeof v === 'object') visit(v);
                }
            };
            visit(data);

            if (this.isValidSkyPosition(raDeg, decDeg)) {
                const raHours = raDeg / 15.0;
                return {
                    name: name || objectName,
                    ra_deg: raDeg,
                    dec_deg: decDeg,
                    ra_hours: raHours,
                    dec_degrees: decDeg,
                    object_type: otype || 'Unknown',
                    catalog: 'SIMBAD',
                    description: `${name || objectName} (SIMBAD sim-id JSON)`,
                    source: 'SIMBAD'
                };
            }
            return null;
        } catch (e) {
            console.warn('SIMBAD sim-id JSON parse failed:', e);
            return null;
        }
    }

    /**
     * Robust resolver: try multiple variants and multiple SIMBAD endpoints.
     */
    async getObjectCoordinatesRobust(objectName) {
        const candidates = this.nameVariants(objectName);
        console.log(`Trying name variants for '${objectName}':`, candidates);

        for (const cand of candidates) {
            // Try script first (most flexible for aliases)
            const { result: viaScript, unknown } = await this.queryScriptResolver(cand);
            if (viaScript) return viaScript;
            // SIMBAD has no object by this identifier; the endpoints below resolve names the same way
            if (unknown) continue;

            // Try basic page scrape
            const viaBasic = await this.getObjectCoordinates(cand);
            if (viaBasic) return viaBasic;

            // Try sim-id JSON endpoint
            const viaJson = await this.getObjectCoordinatesSimIdJSON(cand);
            if (viaJson) return viaJson;

            // Try TAP ident join
            try {
                const adql = `SELECT TOP 1 b.main_id, b.ra, b.dec, b.otype_txt
                              FROM basic AS b
                              JOIN ident AS i ON i.oidref = b.oidref
                              WHERE i.id = '${cand.replace(/'/g, "''")}'`;
                const params = new URLSearchParams({
                    REQUEST: 'doQuery', LANG: 'ADQL', FORMAT: 'json', QUERY: adql
                });
                let response;
                try {
                    response = await fetch(`${this.baseUrl}?${params}`);
                } catch {
                    response = await fetch(`${this.corsProxy}${encodeURIComponent(this.baseUrl)}?${params}`);
                }
                if (response && response.ok) {
                    const data = await response.json();
                    if (data.data && data.data.length > 0) {
                        const row = data.data[0];
                        const raHours = parseFloat(row[1]) / 15.0;
                        const decDeg = parseFloat(row[2]);
                        if (!this.isValidSkyPosition(raHours * 15.0, decDeg)) continue;
                        return {
                            name: row[0] || cand,
                            ra_deg: raHours * 15.0,
                            dec_deg: decDeg,
                            ra_hours: raHours,
                            dec_degrees: decDeg,
                            object_type: row[3] || 'Unknown',
                            catalog: 'SIMBAD',
                            description: `${cand} (SIMBAD ident)`,
                            source: 'SIMBAD'
                        };
                    }
                }
            } catch (e) {
                console.warn('SIMBAD TAP ident fallback failed for', cand, e);
            }
        }
        return null;
    }

    /**
     * Search for multiple objects matching a pattern
     * @param {string} pattern - Search pattern
     * @param {number} maxResults - Maximum number of results
     * @returns {Promise<Array>} Array of matching objects
     */
    async searchObjects(pattern, maxResults = 10) {
        try {
            console.log(`Searching SIMBAD for pattern: ${pattern}`);
            
            const query = `SELECT TOP ${maxResults} main_id, ra, dec, otype_txt 
                          FROM basic 
                          WHERE main_id LIKE '%${pattern}%' 
                          ORDER BY main_id`;
            
            const params = new URLSearchParams({
                REQUEST: 'doQuery',
                LANG: 'ADQL',
                FORMAT: 'json',
                QUERY: query
            });

            let response;
            try {
                response = await fetch(`${this.baseUrl}?${params}`);
            } catch (error) {
                response = await fetch(`${this.corsProxy}${encodeURIComponent(this.baseUrl)}?${params}`);
            }

            if (!response.ok) {
                throw new Error(`SIMBAD search failed: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.data && data.data.length > 0) {
                return data.data.map(obj => ({
                    name: obj[0],
                    display_name: obj[0],
                    ra_hours: parseFloat(obj[1]) / 15.0,
                    dec_degrees: parseFloat(obj[2]),
                    object_type: obj[3] || 'Unknown',
                    catalog: 'SIMBAD',
                    source: 'SIMBAD'
                }));
            }
            
            return [];
        } catch (error) {
            console.error('SIMBAD search error:', error);
            return [];
        }
    }
}
